import axios from "axios";
import { useTranslation } from "react-i18next";
import { useReadLocalStorage } from "usehooks-ts";
import useAnalytics from "~/hooks/useAnalytics";
import Button from "~/ui/Button/Button";
import { normalizeMarketIntelligenceData } from "~/utils/normalize.utils";

interface DocumentItemProps {
    item: SourceDocument;
}
export interface SourceDocument {
    SourceDocumentID: number;
    CategoryTitle: string;
    CategoryDescription: string;
    CategoryTags: string;
    ThumbnailImage: string;
}

const DocumentItem = ({ item }: DocumentItemProps) => {
  const normalizedItem = normalizeMarketIntelligenceData(item);

  const {
    categoryTitle,
    categoryTags,
    categoryDescription,
    thumbnailImage,
    sourceDocumentID,
  } = normalizedItem;

  const token = useReadLocalStorage("_t") as string;
  const { t } = useTranslation();
  const { sp_click } = useAnalytics();

  const downloadDocument = () => {
    sp_click("N", "download", "Market Intelligence Document download", categoryTitle, {
      _title: "Explore Market Intelligence results",
    });

    return axios
      .get(`${import.meta.env.VITE_API_HOST}/api/download/document/id?documentID=${sourceDocumentID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: response.headers["content-type"] })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", categoryTitle || "pdf");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  };

  const previewDocument = () => {
    return axios
      .get(`${import.meta.env.VITE_API_HOST}/api/download/document/id?documentID=${sourceDocumentID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: response.headers["content-type"] })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  };

  return (
    <div className="flex w-full max-w-[660px] h-[240px] bg-white rounded-md shadow-sm overflow-hidden">
      {/* Thumbnail */}
      <div className="bg-stone-lighter w-[208px] h-full p-4 flex items-center justify-center">
        <img
          src={`${import.meta.env.VITE_API_HOST}${thumbnailImage}`}
          alt="Thumbnail"
          className="w-[146px] max-h-[208px] object-contain"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col justify-between flex-1 px-4 py-6 gap-4">
        {/* Title & Tags */}
        <div>
          <h2 className="text-lg text-stone-darkest font-medium-light leading-7 tracking-[-0.02em] line-clamp-2">
            {categoryTitle}
          </h2>
          {categoryTags && (
            <p className="mt-1 text-sm text-stone-darkest font-medium-light leading-5 tracking-[-0.02em]">
              {categoryTags}
            </p>
          )}
        </div>

        {/* Description */}
        <p className="text-xs text-stone-vibrant text-justify font-medium-light leading-4 line-clamp-4">
          {categoryDescription}
        </p>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button
            icon="download"
            className="!bg-salmon-primary w-[121px] h-[36px] text-sm !text-stone-lightest font-medium-light leading-5 tracking-[-0.02em]"
            onPress={downloadDocument}
          >
            {t("download")}
          </Button>
          <Button
            icon="visibility"
            className="!bg-stone-soft w-[121px] h-[36px] text-sm !text-stone-darkest font-medium-light leading-5 tracking-[-0.02em]"
            onPress={previewDocument}
          >
            {t("preview")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentItem;
